// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Meter } from '@/components/greenlight/Meter';
import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { SegmentedHealth, type HealthSegment } from '@/components/greenlight/SegmentedHealth';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  QUEUE_BACKLOG_ALERT_SECONDS,
  type HealthSnapshot,
  type HealthWorkerTaskAttention,
} from '@greenlight/shared';

import { knownVersion, releaseLabel, shortCommit } from './versionLabels';

/**
 * A card whose section the snapshot did not carry. An older reporter that
 * predates a field has not said anything about it, which is neither zero nor
 * healthy.
 */
export function NotReportedCard({
  title,
  testId,
  className,
}: {
  readonly title: string;
  readonly testId?: string;
  readonly className?: string;
}): React.ReactElement {
  return (
    <SectionCard
      title={title}
      {...(testId ? { testId } : {})}
      {...(className ? { className } : {})}
    >
      <p className="text-sm text-muted-foreground">Not reported.</p>
    </SectionCard>
  );
}

export function VersionsCard({
  versions,
}: {
  readonly versions: HealthSnapshot['versions'];
}): React.ReactElement {
  // Install identity: one release and one commit for the whole install. Older
  // snapshots carry the commit only, under `control_plane_image`.
  const release = knownVersion(versions.release_version);
  const sourceCommit =
    knownVersion(versions.source_commit) ?? knownVersion(versions.control_plane_image);

  // The release version is the identity of the whole install: the server image,
  // the installer package, and the chart all ship together under it, so a
  // per-component digest beside it is a second answer to a question that has
  // one. Components whose version can disagree with the release keep their own
  // row; the ones that cannot are the release.
  const rows: [string, string][] = [
    ...(release !== undefined
      ? ([['Release version', releaseLabel(release)]] as [string, string][])
      : []),
    ...(sourceCommit !== undefined
      ? ([['Release commit', shortCommit(sourceCommit)]] as [string, string][])
      : []),
    ['Database schema', versions.db_migration],
  ];

  return (
    <SectionCard title="Versions" testId="versions">
      <dl className="space-y-0.5 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-2 py-1">
            <dt className="text-muted-foreground shrink-0">{label}</dt>
            <dd className="min-w-0">
              <MonoValue className="truncate max-w-[160px]">{value}</MonoValue>
            </dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  );
}

/**
 * Utilization at which connection pooling gets extracted in front of the
 * app-data server, sustained 24h (docs/09-data-brokering.md).
 */
const APPS_DB_POOLING_TRIGGER = 0.7;

export function PostgresCard({
  postgres,
  history,
}: {
  readonly postgres: HealthSnapshot['postgres'];
  /** The card's own history, collapsed below its current readings. */
  readonly history?: React.ReactNode;
}): React.ReactElement {
  return (
    <SectionCard title="Postgres connections" testId="postgres-connections">
      <div className="space-y-3 text-sm">
        {(
          [['Control', postgres.control] as const, ['Apps', postgres.apps] as const] as Array<
            [string, { active_connections: number; max_connections: number }]
          >
        ).map(([name, db]) => (
          <div key={name}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-muted-foreground">{name}</span>
              <span className="font-mono text-foreground">
                {db.active_connections} / {db.max_connections}
              </span>
            </div>
            <Meter
              value={db.active_connections}
              target={db.max_connections}
              warn={Math.round(
                db.max_connections * (name === 'Apps' ? APPS_DB_POOLING_TRIGGER : 0.7),
              )}
              crit={Math.round(db.max_connections * 0.9)}
            />
          </div>
        ))}
      </div>
      {history}
    </SectionCard>
  );
}

/**
 * What the queue card says, or nothing at all.
 *
 * Graphile task identifiers (`crashloop.detect`) are implementation detail: a
 * reader cannot act on a job name, and the list invites reading a scheduled
 * every-minute job as a fault. Four things are actionable — work is backing up,
 * work is failing, work is being held, or work gave up — so the card says those
 * in a sentence and is omitted entirely when none is true.
 *
 * Held and exhausted work is why the card cannot key on backlog and failures
 * alone: a job a worker still holds is not pending, and one that spent its last
 * attempt without recording an error is not a failure, so both were invisible
 * here while a pipeline sat waiting on them.
 */
export function queueMessages(queue: HealthSnapshot['queue']): string[] {
  const messages: string[] = [];
  if (queue.oldest_pending_age_s >= QUEUE_BACKLOG_ALERT_SECONDS) {
    messages.push(
      `Background work is backed up — the oldest job has waited ${queue.oldest_pending_age_s} seconds (limit ${QUEUE_BACKLOG_ALERT_SECONDS}).`,
    );
  }
  const held = queue.locked_over_duration ?? 0;
  if (held > 0) {
    const jobs = held === 1 ? 'job has' : 'jobs have';
    // "Long-running", not "abandoned": duration alone does not prove the worker
    // holding the job is gone, and only ownership evidence can say that.
    messages.push(`${held} long-running background ${jobs} been in progress for over 15 minutes.`);
  }
  const abandoned = queue.abandoned ?? 0;
  if (abandoned > 0) {
    const jobs = abandoned === 1 ? 'job was' : 'jobs were';
    messages.push(
      `${abandoned} background ${jobs} held by a worker that stopped; Greenlight is releasing them.`,
    );
  }
  const exhausted = queue.exhausted ?? 0;
  if (exhausted > 0) {
    const jobs = exhausted === 1 ? 'job' : 'jobs';
    messages.push(
      `${exhausted} background ${jobs} gave up after the last retry in the past 24 hours.`,
    );
  }
  if (queue.failed_last_1h > 0) {
    const jobs = queue.failed_last_1h === 1 ? 'job' : 'jobs';
    messages.push(`${queue.failed_last_1h} background ${jobs} failed in the last hour.`);
  }
  return messages;
}

/** One sentence for a scheduled job that is failing or overdue, by its label. */
export function workerTaskMessage(item: HealthWorkerTaskAttention): string {
  const since = item.since === null ? null : new Date(item.since).toLocaleString();
  if (item.state === 'failing') {
    const times =
      item.consecutive_failures === 1 ? 'once' : `${item.consecutive_failures} times in a row`;
    return `${item.label} has failed ${times}${since === null ? '' : ` since ${since}`}.`;
  }
  return since === null
    ? `${item.label} has not completed since it was due.`
    : `${item.label} has not completed since ${since}.`;
}

/**
 * Queue health, only when there is something to act on. A healthy queue has
 * nothing to say, and a depth-by-task-name list says it with job identifiers
 * nobody outside this repo can read. Scheduled jobs that are failing or overdue
 * are listed by what they do, each with its latest failed run's log.
 */
export function QueueCard({
  queue,
  trend,
  workerTasks = [],
  onOpenFailureLog,
}: {
  readonly queue: HealthSnapshot['queue'];
  readonly trend?: number[];
  readonly workerTasks?: readonly HealthWorkerTaskAttention[];
  readonly onOpenFailureLog?: (item: HealthWorkerTaskAttention) => void;
}): React.ReactElement | null {
  const notes = queueMessages(queue);
  if (notes.length === 0 && workerTasks.length === 0) return null;
  return (
    <SectionCard title="Queue health" testId="queue-health">
      <div className="space-y-2">
        {notes.map((note) => (
          <p key={note} className="text-sm leading-relaxed text-foreground">
            {note}
          </p>
        ))}
        {workerTasks.length > 0 ? (
          <ul className="space-y-1" aria-label="Scheduled jobs needing attention">
            {workerTasks.map((item) => (
              <li
                key={item.label}
                className="flex flex-wrap items-baseline gap-x-2 text-sm leading-relaxed text-foreground"
              >
                <span>{workerTaskMessage(item)}</span>
                {item.failure_log_id !== null && onOpenFailureLog ? (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => onOpenFailureLog(item)}
                  >
                    View the last failed run
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {trend !== undefined ? (
          <MetricSparkline data={trend} className="mt-2" ariaLabel="Queue depth trend" />
        ) : null}
      </div>
    </SectionCard>
  );
}

// Status word for each provider resource health value — paired with a dot so
// status is conveyed by text and shape, not color alone (WCAG 2.1 AA).
const PROVIDER_STATUS_WORD: Record<'available' | 'degraded' | 'unavailable', string> = {
  available: 'Available',
  degraded: 'Degraded',
  unavailable: 'Unavailable',
};

const PROVIDER_DOT_CLASS: Record<'available' | 'degraded' | 'unavailable', string> = {
  available: 'bg-[hsl(var(--status-green))]',
  degraded: 'bg-[hsl(var(--status-amber))]',
  unavailable: 'bg-[hsl(var(--status-red))]',
};

// Display labels for the cloud resource health tiles, keyed by the snapshot's
// platform-role keys (docs/11) — cloud-neutral, so the same page reads
// correctly on an Azure or GCP install.
const RESOURCE_LABELS: Record<string, string> = {
  compute: 'Compute cluster',
  postgres_control: 'Postgres · control',
  postgres_apps: 'Postgres · apps',
  vault: 'Secrets vault',
  registry: 'Container registry',
  storage: 'Object storage',
  dns: 'DNS',
};

function ResourceTile({
  label,
  status,
}: {
  label: string;
  status: 'available' | 'degraded' | 'unavailable';
}): React.ReactElement {
  return (
    <div className="bg-card px-4 py-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </span>
      <div className="mt-1 flex items-center gap-1.5">
        <span
          className={cn('size-1.5 shrink-0 rounded-full', PROVIDER_DOT_CLASS[status])}
          aria-hidden="true"
        />
        <span className="text-[13px] text-foreground">{PROVIDER_STATUS_WORD[status]}</span>
      </div>
    </div>
  );
}

export function CloudResourcesCard({
  health,
}: {
  readonly health: HealthSnapshot['provider_resource_health'];
}): React.ReactElement {
  const entries = Object.entries(health);
  const available = entries.filter(([, v]) => v === 'available').length;
  return (
    <SectionCard
      title="Cloud resource health"
      testId="cloud-resource-health"
      badge={
        <span className="text-xs text-muted-foreground">
          {available} of {entries.length} available
        </span>
      }
      className="md:col-span-2"
    >
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No resources tracked.</p>
      ) : (
        <div className="overflow-hidden rounded-[8px] border border-border bg-border">
          <div className="grid grid-cols-2 gap-px md:grid-cols-4">
            {entries.map(([resource, status]) => (
              <ResourceTile
                key={resource}
                label={RESOURCE_LABELS[resource] ?? resource}
                status={status}
              />
            ))}
            {Array.from({ length: (4 - (entries.length % 4)) % 4 }).map((_, i) => (
              <div key={`filler-${i}`} className="bg-card" aria-hidden="true" />
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

export function AppHealthCard({
  countByStatus,
  className,
}: {
  readonly countByStatus: HealthSnapshot['apps']['count_by_status'];
  readonly className?: string;
}): React.ReactElement {
  const segments: HealthSegment[] = [
    { key: 'active', label: 'active', count: countByStatus.active ?? 0, tone: 'green' },
    { key: 'degraded', label: 'degraded', count: countByStatus.degraded ?? 0, tone: 'amber' },
    { key: 'failed', label: 'failed', count: countByStatus.failed ?? 0, tone: 'red' },
    // Paused/inactive sits last, matching the Apps fleet-health lifecycle order.
    {
      key: 'suspended',
      label: 'suspended',
      count: countByStatus.suspended ?? 0,
      tone: 'neutral',
    },
  ];
  return (
    <SegmentedHealth
      title="App health"
      unitLabel="apps"
      total={segments.reduce((sum, s) => sum + s.count, 0)}
      segments={segments}
      {...(className ? { className } : {})}
    />
  );
}
